<template>
  <div class="xmodal-wrapper" id="search-page-filters">
    <div class="xmodal-inner">
      <div class="xmodal">
        <div
          class="xmodal-close icon-x"
          @click="searchPageStore.showAdvancedSearchModal = false"
        ></div>
        <h2 class="xmodal-header">Advanced Search</h2>
        <div class="form-container">
          <div class="row sc-form">
            <div class="sc-form-field-wrapper">
              <div class="sc-form-field">
                <label for="filterParameterId"
                  >Filter Parameters<i class="required">*</i></label
                >
                <vee-field
                  as="select"
                  id="filterParameterId"
                  name="filterParameterId"
                  :class="{
                    'is-invalid': errors.filterParameterId,
                  }"
                >
                  <option :value="undefined">&nbsp;</option>
                  <option
                    v-for="option in uncategorizedSearchTypes"
                    :key="option.id"
                    :value="option.id"
                    :title="option.description"
                  >
                    {{ option.label }}
                  </option>
                  <optgroup label="Name">
                    <option
                      v-for="option in nameSearchTypes"
                      :key="option.id"
                      :value="option.id"
                      :title="option.description"
                    >
                      {{ option.label }}
                    </option>
                  </optgroup>

                  <optgroup label="Email">
                    <option
                      v-for="option in emailSearchTypes"
                      :key="option.id"
                      :value="option.id"
                      :title="option.description"
                    >
                      {{ option.label }}
                    </option>
                  </optgroup>
                </vee-field>
              </div>
              <div class="error-wrapper">
                <vee-error-message
                  name="filterParameterId"
                  class="error-message"
                />
              </div>
            </div>

            <div class="sc-form-field-wrapper">
              <div class="sc-form-field">
                <div class="sc-radio-group">
                  <div
                    class="sc-radio"
                    v-for="operation in operators"
                    :key="operation.id"
                    :class="{ disabled: isOperatorDisabled(operation.id) }"
                  >
                    <vee-field
                      type="radio"
                      name="operationId"
                      :value="operation.id"
                      :disabled="isOperatorDisabled(operation.id)"
                      required
                      :class="{
                        'is-invalid': errors.operationId,
                      }"
                    ></vee-field>
                    <span> {{ operation.label }}</span>
                  </div>
                </div>
                <div class="error-wrapper">
                  <vee-error-message name="operationId" class="error-message" />
                </div>
              </div>
            </div>
            <div class="sc-form-field-wrapper">
              <div class="sc-form-field">
                <label for="searchTerm">Search<i class="required">*</i></label>
                <vee-field
                  type="text"
                  id="searchTerm"
                  name="searchTerm"
                  :class="{
                    'is-invalid': errors.searchTerm,
                  }"
                />
              </div>
              <div class="error-wrapper">
                <vee-error-message name="searchTerm" class="error-message" />
              </div>
            </div>
          </div>

          <div class="row sc-form"></div>
          <div v-show="allowMultipleQueries">
            <ul>
              <li v-for="(query, index) in queries" :key="index">
                <div class="row">
                  <div class="column">
                    <button
                      class="delete-btn icon-x"
                      @click="removeRule(index)"
                    ></button>
                  </div>

                  {{ query.searchFilter?.label }}: {{ query.searchTerm }}
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div class="form-buttons">
          <button
            :disabled="submitButtonDisabled"
            class="xedit"
            @click="filterAndSearch"
          >
            Apply Filter and Search
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useSearchPageStore } from "@/store/store/searchPageStore";
import { ref, computed, watch } from "vue";
import {
  Field as VeeField,
  Form as VeeForm,
  ErrorMessage as VeeErrorMessage,
  useForm,
} from "vee-validate";
import * as yup from "yup";
import { SearchTypes, Operators } from "@/types/advanced-search";
import { useRouter, useRoute } from "vue-router";
import functions from "@/use/functions.js";

const route = useRoute();

interface SearchFilter {
  id: number;
  name: string;
  label?: string;
  description: string;
  category?: string;
  visible?: boolean;
}

interface SearchQuery {
  searchFilter?: SearchFilter;
  filterParameterId: number;
  searchType: number;
  searchTerms: string[];
  searchTerm: string;
  operationId: number;
}

interface SearchOperation {
  id: number;
  name: string;
  label?: string;
  description: string;
}

const emits = defineEmits<{
  (e: "addQueries", rule: SearchQuery[]): void;
}>();

const searchPageStore = useSearchPageStore();

const allowMultipleQueries = ref<boolean>(false);
const submitButtonDisabled = ref<boolean>(false);
const showError = ref<boolean>(false);

const searchTypes = computed<Array<SearchFilter>>(() => [
  {
    id: SearchTypes.APPLICATION_ID,
    name: "applicationId",
    label: "Application ID",
    description: "Application ID (referenceId)",
    visible: true,
  },
  {
    id: SearchTypes.SSN,
    name: "ssn",
    label: "SSN",
    description: "SSN",
    visible: true,
  },
  {
    id: SearchTypes.CLUID,
    name: "cluid",
    label: "CLUID",
    description: "CLUID",
    visible: searchPageStore.enableSearchOnCLUID,
  },
  {
    id: SearchTypes.NAME,
    name: "ApplicantNamePartialMatch",
    label: "Applicant Name",
    description:
      "Primary Applicant or Co-Applicant Name matches on first or last name",
    category: "Name",
    visible: true,
  },
  {
    id: SearchTypes.NAME_EXACT,
    name: "ApplicantNameExactMatch",
    label: "Applicant Name - Exact Match",
    description:
      "Primary Applicant or Co-Applicant Name matches on full name as entered (First Last)",
    category: "Name",
    visible: false,
  },
  {
    id: SearchTypes.PRIMARY_APPLICANT_NAME,
    name: "primaryApplicantName",
    label: "Primary Applicant Name",
    description: "Primary Applicant Name matches on first or last name",
    category: "Name",
    visible: true,
  },
  {
    id: SearchTypes.PRIMARY_APPLICANT_NAME_EXACT,
    name: "primaryApplicantNameExact",
    label: "Primary Applicant Name - Exact Match",
    description:
      "Primary Applicant Name matches on full name as entered (First Last)",
    category: "Name",
    visible: false,
  },
  {
    id: SearchTypes.CO_APPLICANT_NAME,
    name: "coApplicantName",
    label: "Co-Applicant Name",
    description: "Co-Applicant Name matches on first or last name",
    category: "Name",
    visible: true,
  },
  {
    id: SearchTypes.CO_APPLICANT_NAME_EXACT,
    name: "coApplicantNameExact",
    label: "Co-Applicant Name - Exact Match",
    description:
      "Co-Applicant Name matches on full name as entered (First Last)",
    category: "Name",
    visible: false,
  },
  {
    id: SearchTypes.EMAIL,
    name: "ApplicantEmailPartial",
    label: "Email Address",
    description:
      "Primary Applicant or Co-Applicant Email Address contains search term",
    category: "Email",
    visible: true,
  },
  {
    id: SearchTypes.PRIMARY_APPLICANT_EMAIL,
    name: "PrimaryApplicantEmailPartialMatch",
    label: "Primary Applicant Email",
    description: "Primary Applicant Email contains search term",
    category: "Email",
    visible: true,
  },
  {
    id: SearchTypes.CO_APPLICANT_EMAIL,
    name: "CoApplicantEmailPartialMatch",
    label: "Co-Applicant Email",
    description: "Co-Applicant Email contains search term",
    category: "Email",
    visible: true,
  },
]);

const operators = ref<Array<SearchOperation>>([
  {
    id: Operators.EXACT_MATCH as number,
    name: "ExactMatch",
    label: "Exact Match",
    description: "Exact Match",
  },
  {
    id: Operators.PARTIAL_MATCH as number,
    name: "PartialMatch",
    label: "Partial Match",
    description: "Partial Match",
  },
]);

const uncategorizedSearchTypes = computed(() =>
  searchTypes.value
    .filter(
      (type) =>
        type.category !== "Email" && type.category !== "Name" && type.visible,
    )
    .sort((a, b) => a.label!.localeCompare(b.label!)),
);

const nameSearchTypes = computed(() =>
  searchTypes.value.filter((type) => type.category === "Name" && type.visible),
);

const emailSearchTypes = computed(() =>
  searchTypes.value.filter((type) => type.category === "Email" && type.visible),
);

const queries = ref<Array<SearchQuery>>([]);

const newQuery = ref<SearchQuery>({
  searchType: 0, // the actual search type used in the search
  searchTerm: "",
  searchTerms: [],
  filterParameterId: 0, // what the user selects
  searchFilter: undefined,
  operationId: 0,
});

const validationSchema = yup.object({
  filterParameterId: yup
    .number()
    .min(1, "A filter parameter is required")
    .transform((value) => (isNaN(value) ? null : value))
    .required("A filter parameter is required"),
  operationId: yup
    .number()
    .min(1, "An operation is required")
    .required("An operation is required"),
  searchTerm: yup
    .string()
    .transform((curr, orig) => (orig === "" ? null : curr))
    .required("A search term is required"),
  searchTerms: yup
    .array()
    .of(yup.string())
    .required("Search terms are required"),
  searchType: yup.number().required("A selection is required"),
});

const { values, setFieldValue, resetForm, errors, validate } = useForm({
  validationSchema,
  initialValues: newQuery.value,
});

function getSearchFilterById(id: number): SearchFilter | undefined {
  return searchTypes.value.find((filter) => filter.id === id);
}

function isOperatorDisabled(operatorId: number): boolean {
  // Disable Partial Match for SSN
  if (
    values.filterParameterId === SearchTypes.SSN &&
    operatorId === Operators.PARTIAL_MATCH
  ) {
    return true;
  }

  return false;
}

function handleFilterParamChange() {
  // if searching by SSN - require exact match
  if (values.filterParameterId === SearchTypes.SSN) {
    setFieldValue("operationId", Operators.EXACT_MATCH);
  }

  let id = values.filterParameterId;
  let filter = getSearchFilterById(id);

  setFieldValue("searchType", id);
  setFieldValue("searchFilter", filter);
}

function handleOperationChange(operationId: number) {
  // Check if the operation is exact match or partial match and adjust the search type accordingly
  setSearchType();
}

function setSearchType() {
  let id = values.filterParameterId;
  let filter = getSearchFilterById(id);

  switch (values.filterParameterId) {
    case SearchTypes.NAME: // Handle exact match
      if (values.operationId === Operators.EXACT_MATCH) {
        id = SearchTypes.NAME_EXACT;
      }
      break;
    case SearchTypes.PRIMARY_APPLICANT_NAME:
      if (values.operationId === Operators.EXACT_MATCH) {
        id = SearchTypes.PRIMARY_APPLICANT_NAME_EXACT;
      }
      break;
    case SearchTypes.CO_APPLICANT_NAME:
      if (values.operationId === Operators.EXACT_MATCH) {
        id = SearchTypes.CO_APPLICANT_NAME_EXACT;
      }
      break;
    default:
      break;
  }

  filter = getSearchFilterById(id);

  setFieldValue("searchType", id);
  setFieldValue("searchFilter", filter);
}

function filterAndSearch() {
  submitButtonDisabled.value = true;
  showError.value = false;

  // Check if valid form input
  if (!values.filterParameterId || !values.operationId || !values.searchTerm) {
    submitButtonDisabled.value = false;
    showError.value = true;

    validate();
    return;
  }

  // Set the searchType based on the operation selected
  setSearchType();

  setFieldValue("searchTerm", values.searchTerm);
  setFieldValue("searchTerms", [values.searchTerm]);

  queries.value.push({ ...values } as SearchQuery);
  emits("addQueries", queries.value);

  // CLOSE MODAL / form Reset handled by parent component
}

function closeModal() {
  searchPageStore.showAdvancedSearchModal = false;
  submitButtonDisabled.value = false;
  showError.value = false;

  resetForm();
}

function removeRule(index: number) {
  if (index !== -1) {
    queries.value.splice(index, 1);
  }
}

function resetQueries() {
  queries.value = [];
}

function getCurrentSearchTerm() {
  return route.query.SearchTerms
    ? Array.isArray(route.query.SearchTerms)
      ? (route.query.SearchTerms as string[])[0]
      : String(route.query.SearchTerms)
    : "";
}

const searchTermsParam = computed<string[]>(() => {
  return JSON.parse(route.query.SearchTerms?.toString() || "[]") || [];
});

const searchTermParam = computed<string>(() => {
  return searchTermsParam.value.length > 0 ? searchTermsParam.value[0] : "";
});

const currentQueryFromUrl = computed(() => {
  const validQuery =
    route.query.SearchType !== undefined &&
    route.query.SearchTerms !== undefined &&
    route.query.SearchOperation !== undefined;
  if (!validQuery) {
    return null;
  }
  return {
    filterParameterId: Number(route.query.SearchType) || 0,
    searchType: Number(route.query.SearchType) || 0,
    searchTerm: getCurrentSearchTerm(),
    searchTerms: route.query.SearchTerms
      ? Array.isArray(route.query.SearchTerms)
        ? (route.query.SearchTerms as string[])
        : [String(route.query.SearchTerms)]
      : [],
    operationId: Number(route.query.SearchOperation) || 0,
    searchFilter: getSearchFilterById(Number(route.query.SearchType) || 0),
  };
});

// watch searchPageStore.showAdvancedSearchModal to reset queries when it is opened
watch(
  () => searchPageStore.showAdvancedSearchModal,
  (newValue) => {
    if (newValue) {
      resetQueries();
      resetForm();
      submitButtonDisabled.value = false;
    }
  },
);

// Watch changes to operation
watch(
  () => values.operationId,
  (newValue) => {
    handleOperationChange(newValue);
  },
);

watch(
  () => values.filterParameterId,
  () => {
    handleFilterParamChange();
  },
);

defineExpose({
  queries,
  resetQueries,
  closeModal,
  currentQueryFromUrl,
});
</script>

<style scoped>
* {
  font-family: "Montserrat", sans-serif;
}
.xmodal-header {
  color: #000;
  font-family: "Montserrat", sans-serif;
  font-size: 25px;
  font-style: normal;
  font-weight: 700;
}
select,
input {
  height: 34px;
  border-radius: 6px;
  border: 1px solid #ccc;
}

option {
  height: 34px;
}

.row {
  display: flex;
  flex-direction: row;
  gap: 10px;
}

.xmodal {
  padding: 16px;
  overflow-x: auto;
}

.xmodal-inner {
  max-width: 992px;
}
div.xmodal-inner {
  width: 100%;
}
.column {
  display: flex;
  align-items: flex-end;
}
.submit-btn {
  height: 34px;
  border-radius: 6px;
  background-color: #000000;
  color: #ffffff;
}

ul {
  list-style: none;
  padding: 0;
}

.summary-rule .searchfilter {
  height: 34px;
  border-radius: 6px;
  border: 1px solid #ccc;
  padding: 16px;
}

.xedit[disabled] {
  background-color: #cccccc;
  border: none;
  cursor: not-allowed;
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
}
p.hint {
  font-size: 10px;
  color: #666666;
  margin: 0;
  padding: 0;
  font-weight: 400;
}

h2 {
  font-weight: 600;
}

.sc-form .sc-form-field-wrapper {
  flex-grow: 1;
}
.sc-form .sc-form-field-wrapper:nth-child(2) {
  flex: 0 0 133px;
  display: flex;
  position: relative;
}
.sc-form .sc-form-field:nth-child(2) {
  position: absolute;
}

.sc-form .sc-form-field {
  position: relative;
}
.error-message {
  color: #b40000;
  font-size: 10px;
  position: absolute;
	font-weight: 400;
}

.sc-form .sc-form-field label {
  font-weight: 600;
}
.sc-form-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  justify-content: flex-end;
}

i.required {
  color: #b40000;
}

.form-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.form-buttons button {
  padding: 11px 16px;
  justify-content: center;
  align-items: center;
  display: flex;
  height: 34px;
  font-family: "Montserrat", sans-serif;
  border-radius: 4px;
  background: #000;
  color: #fff;
  font-size: 14px;
}

.sc-radio-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sc-radio {
  display: flex;
  align-items: center;
  gap: 6px;
}

.sc-radio label, .sc-radio span {
  color: #000;
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
}

.sc-form .sc-form-field:nth-child(2) .sc-radio-group {
  position: relative;
}

input[type="radio"] {
  height: 18px;
  width: 18px;
}

.sc-radio.disabled input[type="radio"][disabled] {
  cursor: not-allowed;
}

.sc-radio.disabled span {
  color: #999999;
  cursor: not-allowed;
}

@media (max-width: 600px) {

	.form-buttons button {
		height: auto;
	}
  .sc-form {
    flex-direction: column;
  }
  .error-message {
    position: relative;
  }
  .sc-form .sc-form-field-wrapper:nth-child(2) {
    flex: 1 0 auto;
    justify-content: flex-start;
    margin-top: 10px;
  }
  .sc-form .sc-form-field:nth-child(2) {
    position: static;
  }
}
</style>
